import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PipelineEnvironment, StepTask } from '@telespot/domain';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, of } from 'rxjs';

export interface PipelineUsageEntry {
  id: string;
  name: string | { [key: string]: string };
  stepId: string;
  stepOrder: number;
  description: { [key: string]: string };
}

export interface PipelineRowEvent {
  algorithmId: string;
  pipeline: PipelineUsageEntry;
}

@Component({
    selector: 'ts-algorithm-usage-table',
    templateUrl: './usage-table.component.html',
    styleUrls: ['./usage-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, MatIconModule, MatTooltipModule, TranslateModule, MatTableModule]
})
export class AlgorithmUsageTableComponent {
  @Input() algorithmId: string;
  @Input() pipelines: Record<string, PipelineUsageEntry> = {};
  @Input() language = 'en';
  @Input() noItems = false;

  @Output() editClicked = new EventEmitter<PipelineRowEvent>();
  @Output() deleteClicked = new EventEmitter<PipelineRowEvent>();
  @Output() navigateToPipeline = new EventEmitter<string>();

  readonly displayedColumns = ['name', 'task', 'env', 'actions'];

  constructor(private translateService: TranslateService) {}

  /**
   * Resolves a name that can be either a string or a language dictionary.
   */
  resolveName(name: string | { [key: string]: string }): string {
    if (typeof name === 'string') {
      return name;
    }
    return name[this.language] ?? name['en'] ?? Object.values(name)[0] ?? '';
  }

  /**
   * Resolves a task into a human readable string.
   */
  resolveTask(task: StepTask): Observable<string> {
    switch (task) {
      case StepTask.POSITION:
        return this.translateService.stream('table.algorithms.columns.tasks.position');
      case StepTask.TEXT:
        return this.translateService.stream('table.algorithms.columns.tasks.text');
      case StepTask.OCR:
        return this.translateService.stream('table.algorithms.columns.tasks.ocr');
      case StepTask.CLASSIFICATION:
        return this.translateService.stream('table.algorithms.columns.tasks.classification');
      case StepTask.ROIDETECTION:
        return this.translateService.stream('table.algorithms.columns.tasks.roi_detection');
      case StepTask.SEGMENTATION:
        return this.translateService.stream('table.algorithms.columns.tasks.segmentation');
      case StepTask.ROITRACKING:
        return this.translateService.stream('table.algorithms.columns.tasks.roi_tracking');
      case StepTask.MAPPING:
        return this.translateService.stream('table.algorithms.columns.tasks.mapping');
      default:
        return of(task);
    }
  }

  /**
   * Resolves an environment into a human readable string.
   */
  resolveEnvironment(environment: PipelineEnvironment): string {
    switch (environment) {
      case PipelineEnvironment.CLOUD:
        return this.translateService.instant('core.cloud');
      case PipelineEnvironment.EDGE:
        return this.translateService.instant('core.edge');
    }
  }

  /**
   * Returns the list of pipelines as an array.
   */
  get pipelineList(): PipelineUsageEntry[] {
    return Object.values(this.pipelines);
  }

  onEditClick(pipeline: PipelineUsageEntry): void {
    this.editClicked.emit({ algorithmId: this.algorithmId, pipeline });
  }

  onDeleteClick(pipeline: PipelineUsageEntry): void {
    this.deleteClicked.emit({ algorithmId: this.algorithmId, pipeline });
  }
}
